import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, ArrowRight, ArrowLeft, Check, CheckCircle2, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { SERVICES } from "@/data/services";
import { CAR_BRANDS, SITE } from "@/data/site";
import { ServiceIcon } from "@/components/icons";
import Reveal from "@/components/motion/Reveal";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const YEARS = Array.from({ length: new Date().getFullYear() - 1994 }, (_, i) => String(new Date().getFullYear() - i));
const STEPS = ["Leistung", "Fahrzeug", "Kontakt"];

export default function QuickQuote() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [data, setData] = useState({
    service: "", brand: "", model: "", year: "", name: "", phone: "",
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = () => {
    if (step === 0 && !data.service) return toast.error("Bitte wähle eine Leistung.");
    if (step === 1 && !data.brand) return toast.error("Bitte wähle mindestens die Marke.");
    setStep((s) => Math.min(s + 1, 2));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!data.name || !data.phone) return toast.error("Bitte Name und Telefon angeben.");
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: data.name,
        phone: data.phone,
        email: "keine Angabe (Schnellanfrage)",
        service: data.service,
        vehicle: [data.brand, data.model, data.year].filter(Boolean).join(" · "),
        message: `60-Sekunden-Schnellanfrage · Leistung: ${data.service} · Fahrzeug: ${[data.brand, data.model, data.year].filter(Boolean).join(" ") || "k. A."}`,
      });
      setDone(true);
      toast.success("Anfrage gesendet! Wir rufen dich schnellstmöglich zurück.");
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte ruf uns direkt an.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-[#0a0a0a] border-white/10 text-white placeholder:text-neutral-600 focus-visible:ring-volt rounded-sm h-12";
  const progress = done ? 100 : ((step + 1) / 3) * 100;

  return (
    <section id="schnellanfrage" className="relative bg-[#050505] py-24 sm:py-32">
      <div className="absolute inset-0 radial-glow" />
      <div className="relative mx-auto max-w-[900px] px-5 sm:px-8">
        <Reveal className="mb-10 text-center">
          <span className="inline-flex items-center gap-2 border border-volt/40 bg-volt/10 px-4 py-1.5 font-body text-[11px] font-bold uppercase tracking-[0.2em] text-volt">
            <Zap className="h-4 w-4" /> In ~60 Sekunden
          </span>
          <h2 className="mt-6 font-heading text-4xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-6xl">
            Sofort-<span className="text-volt">Anfrage</span>
          </h2>
          <p className="mx-auto mt-4 max-w-lg font-body text-base text-neutral-400">
            Drei kurze Schritte – wir melden uns mit einem fairen Angebot zurück.
            Kein Papierkram, keine Wartezeit.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="border border-white/10 bg-[#0a0a0a] p-6 sm:p-10" data-testid="quickquote">
            {/* progress */}
            <div className="mb-8">
              <div className="mb-3 flex items-center justify-between">
                {STEPS.map((label, i) => (
                  <div key={label} className="flex items-center gap-2">
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full font-body text-xs font-bold ${
                      done || i < step ? "bg-volt text-black" : i === step ? "bg-volt text-black" : "bg-white/10 text-neutral-500"
                    }`}>
                      {done || i < step ? <Check className="h-4 w-4" strokeWidth={3} /> : i + 1}
                    </span>
                    <span className={`hidden font-body text-xs font-semibold uppercase tracking-wide sm:inline ${i === step && !done ? "text-white" : "text-neutral-500"}`}>{label}</span>
                  </div>
                ))}
              </div>
              <div className="h-1 w-full overflow-hidden bg-white/10">
                <motion.div className="h-full bg-volt" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
              </div>
            </div>

            {done ? (
              <div className="flex flex-col items-center py-10 text-center" data-testid="quickquote-success">
                <CheckCircle2 className="h-16 w-16 text-volt" />
                <h3 className="mt-4 font-heading text-3xl font-extrabold uppercase text-white">Anfrage raus!</h3>
                <p className="mt-2 max-w-sm font-body text-neutral-400">
                  Danke, {data.name.split(" ")[0]}! Wir melden uns schnellstmöglich unter deiner Nummer zurück.
                  Notfall? <a href={`tel:${SITE.phoneIntl}`} className="text-volt">{SITE.phoneDisplay}</a>
                </p>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.3 }}
                >
                  {step === 0 && (
                    <div>
                      <p className="mb-4 font-heading text-xl font-bold uppercase text-white">Was brauchst du?</p>
                      <div className="grid gap-2.5 sm:grid-cols-2">
                        {SERVICES.map((s) => {
                          const active = data.service === s.nav;
                          return (
                            <button
                              key={s.slug}
                              type="button"
                              data-testid={`quickquote-service-${s.slug}`}
                              onClick={() => { set("service", s.nav); setTimeout(() => setStep(1), 150); }}
                              className={`flex items-center gap-3 border p-4 text-left transition-all ${
                                active ? "border-volt bg-volt/10" : "border-white/10 bg-[#0d0d0d] hover:border-volt/50"
                              }`}
                            >
                              <ServiceIcon name={s.icon} className={`h-5 w-5 ${active ? "text-volt" : "text-neutral-400"}`} />
                              <span className="font-body text-sm font-semibold text-white">{s.nav}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div>
                      <p className="mb-4 font-heading text-xl font-bold uppercase text-white">Dein Fahrzeug</p>
                      <div className="grid gap-3 sm:grid-cols-3">
                        <Select value={data.brand} onValueChange={(v) => set("brand", v)}>
                          <SelectTrigger data-testid="quickquote-brand" className={inputCls}><SelectValue placeholder="Marke*" /></SelectTrigger>
                          <SelectContent className="max-h-64 bg-[#0a0a0a] border-white/10 text-white">
                            {CAR_BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                            <SelectItem value="Andere">Andere</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input data-testid="quickquote-model" placeholder="Modell (z. B. Golf 7)" value={data.model} onChange={(e) => set("model", e.target.value)} className={inputCls} />
                        <Select value={data.year} onValueChange={(v) => set("year", v)}>
                          <SelectTrigger data-testid="quickquote-year" className={inputCls}><SelectValue placeholder="Baujahr" /></SelectTrigger>
                          <SelectContent className="max-h-64 bg-[#0a0a0a] border-white/10 text-white">
                            {YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div>
                      <p className="mb-4 font-heading text-xl font-bold uppercase text-white">Wie erreichen wir dich?</p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Input data-testid="quickquote-name" placeholder="Name*" value={data.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
                        <Input data-testid="quickquote-phone" placeholder="Telefon*" value={data.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
                      </div>
                      <div className="mt-4 border border-white/10 bg-[#0d0d0d] p-4">
                        <p className="font-body text-xs text-neutral-500">Deine Anfrage</p>
                        <p className="mt-1 font-body text-sm text-neutral-300">
                          <span className="text-volt">{data.service || "—"}</span>
                          {(data.brand || data.model || data.year) && ` · ${[data.brand, data.model, data.year].filter(Boolean).join(" ")}`}
                        </p>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            )}

            {/* nav buttons */}
            {!done && (
              <div className="mt-8 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={back}
                  disabled={step === 0}
                  data-testid="quickquote-back"
                  className="inline-flex items-center gap-2 px-4 py-3 font-body text-sm font-bold uppercase tracking-wide text-neutral-400 transition-colors hover:text-white disabled:invisible"
                >
                  <ArrowLeft className="h-4 w-4" /> Zurück
                </button>

                {step < 2 ? (
                  <button
                    type="button"
                    onClick={next}
                    data-testid="quickquote-next"
                    className="inline-flex items-center gap-2 bg-volt px-8 py-3.5 font-body text-sm font-bold uppercase tracking-wide text-black transition-transform hover:scale-[1.03] glow-volt"
                  >
                    Weiter <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submit}
                    disabled={loading}
                    data-testid="quickquote-submit"
                    className="inline-flex items-center gap-2 bg-volt px-8 py-3.5 font-body text-sm font-bold uppercase tracking-wide text-black transition-transform hover:scale-[1.03] disabled:opacity-60 glow-volt"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
                    {loading ? "Wird gesendet…" : "Rückruf anfordern"}
                  </button>
                )}
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
